/**
 * One subservice-organization card (C6) — #99. CUEC-style card: header tags
 * (treatment · status · Open|Done), an editable AI summary (edited via the
 * parent's Edit pencil), the decision flow, and a per-card Comments thread.
 *
 * Decision (#99): "Request SOC 1" records the decision and opens an A8 action
 * item to obtain it (no placeholder report) — optionally linking an existing
 * report. "Not applicable" records the decision and prompts the rationale as a
 * comment (no separate field). Either decision marks the card Done.
 */
import { useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  ExpandableSection,
  Select,
  SpaceBetween,
  StatusIndicator,
  type SelectProps,
  type StatusIndicatorProps,
} from "@cloudscape-design/components";
import type { Report } from "@/core/api/types";
import { CommentThread } from "@/core/ui/CommentThread";
import { PdfPageLink } from "@/core/ui/PdfPageLink";
import { SubcardHeader } from "@/core/ui/SubcardHeader";
import { decideCarveout } from "./api";

/** First option in the Request-SOC-1 picker: just open an action item (no link). */
export const REQUEST_SENTINEL = "__request__";

/** #C6: how a linked subservice SOC 1's report status reads in the card's FYI line. */
const COVER_STATUS: Record<Report["status"], { type: StatusIndicatorProps["type"]; label: string }> = {
  awaiting_upload: { type: "pending", label: "Awaiting upload" },
  uploading: { type: "in-progress", label: "Uploading" },
  processing: { type: "in-progress", label: "Processing" },
  ready: { type: "success", label: "Ready" },
  error: { type: "error", label: "Error" },
};

export interface SubserviceRow {
  key: string;
  name: string;
  treatment?: string;
  summary?: string;
  page?: number;
  manual?: boolean;
}

export function SubserviceCard({
  co,
  scopeCheckId,
  reportId,
  open,
  decided,
  coveredBy,
  coverName,
  coverStatus,
  reportOptions,
  onEdit,
  onDelete,
  onUpdated,
  onOpenReport,
}: {
  co: SubserviceRow;
  scopeCheckId: string;
  reportId: string;
  /** #119: true iff an OPEN action item targets this carve-out — the single
   *  source of truth for the Open|Done badge (a request-SOC card stays Open). */
  open: boolean;
  decided: boolean | undefined;
  coveredBy?: string;
  coverName?: string;
  /** #C6: the linked SOC 1 report's status — shown as an FYI line (no effect on
   *  this card's Done badge); 'awaiting_upload' is the pending case. */
  coverStatus?: Report["status"];
  reportOptions: SelectProps.Option[];
  onEdit: () => void;
  onDelete: () => void;
  onUpdated: () => void;
  /** #C6: navigate to the linked/placeholder SOC 1 report (to upload or view it). */
  onOpenReport?: (reportId: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chooser, setChooser] = useState(false); // re-show the decision buttons
  const [requesting, setRequesting] = useState(false); // the Request-SOC-1 picker
  const [coverPick, setCoverPick] = useState(REQUEST_SENTINEL);
  const [naPending, setNaPending] = useState(false); // N/A clicked — awaiting the rationale comment

  // #99: an inclusive org is covered by this report — no decision flow. A
  // carved-out org's decision OUTCOME (N/A / requested / linked) drives the
  // verdict label below.
  const inclusive = co.treatment === "inclusive";
  // #C6: the Open|Done BADGE follows the carve-out's A8 action item (the single
  // source of truth, #119). So the card is Open whenever the work is unfinished —
  // undecided, "Request SOC 1" with no SOC 1 in file yet, or a linked SOC 1 that
  // was deleted — and Done only when truly resolved: a SOC 1 is in file, the user
  // documented N/A, or it's inclusive. `done` below still drives the BODY (which
  // verdict tag + which flow to show), independent of the badge.
  const badgeDone = inclusive || !open;
  const done = inclusive || decided !== undefined || !!coveredBy;
  // A previously-documented N/A whose mandatory rationale comment was deleted
  // comes back Open (derive reopened its action item) while its N/A decision is
  // still on record. Treat that as "needs deciding again" so the card shows the
  // decision flow instead of a stale "Documented N/A".
  const reopened = open && decided === false;
  // #109: subservice subcards start collapsed by default; the user expands one
  // to work it. (The decide flow collapses it again once Done.)
  const [expanded, setExpanded] = useState(false);

  const decide = async (applicable: boolean, opts?: { coveringReportId?: string }) => {
    setBusy(true);
    setError(null);
    try {
      await decideCarveout(scopeCheckId, {
        carveout_id: co.key,
        carveout_name: co.name,
        applicable,
        covering_report_id: opts?.coveringReportId ?? null,
      });
      setRequesting(false);
      setChooser(false);
      setNaPending(false);
      setCoverPick(REQUEST_SENTINEL);
      setExpanded(false); // decided → Done, collapse
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  // #99: header verdict for a carved-out org (the treatment badge already
  // conveys "inclusive"); no redundant "Needs decision" tag — Open says it.
  const carvedVerdict =
    done && !inclusive && !reopened
      ? coveredBy
        ? `SOC 1: ${coverName ?? "linked"}`
        : decided === true
          ? "SOC 1 requested"
          : decided === false
            ? "Documented N/A"
            : null
      : null;

  // A carved-out org shows the decision flow when undecided OR reopened (its N/A
  // lost its mandatory comment), so the user can re-document or pick another path.
  const showChooser =
    !inclusive && !requesting && !naPending && (decided === undefined || reopened || chooser);
  const showDecided = !inclusive && !requesting && !chooser && decided !== undefined && !reopened;

  return (
    <ExpandableSection
      variant="container"
      expanded={expanded}
      onChange={({ detail }) => setExpanded(detail.expanded)}
      header={
        <SubcardHeader
          title={co.name}
          status={
            badgeDone ? <Badge color="green">Done</Badge> : <Badge color="red">Open</Badge>
          }
          actions={
            <>
              <Badge color={co.treatment === "carved_out" ? "blue" : "grey"}>
                {co.treatment ?? "unknown"}
              </Badge>
              {carvedVerdict && <Badge color="blue">{carvedVerdict}</Badge>}
              {co.manual && <Badge color="grey">added by management</Badge>}
              {co.page && <PdfPageLink reportId={reportId} page={co.page} />}
              <Button iconName="edit" variant="inline-link" ariaLabel={`Edit ${co.name}`} onClick={onEdit} />
              {co.manual && (
                <Button
                  iconName="remove"
                  variant="inline-link"
                  ariaLabel={`Delete ${co.name}`}
                  onClick={onDelete}
                />
              )}
            </>
          }
        />
      }
    >
      <SpaceBetween size="s">
        {error && (
          <Alert type="error" header="Action failed">
            {error}
          </Alert>
        )}
        {co.summary && (
          <Box>
            <Box variant="awsui-key-label">Summary</Box>
            <Box color="text-body-secondary">{co.summary}</Box>
          </Box>
        )}

        {showDecided && (
          <SpaceBetween direction="horizontal" size="xs">
            <StatusIndicator type={decided === false || coveredBy ? "success" : "info"}>
              {decided === false
                ? "Not applicable — documented"
                : coveredBy
                  ? "SOC 1 requested"
                  : "SOC 1 requested — action item opened to obtain it"}
            </StatusIndicator>
            <Button variant="inline-link" disabled={busy} onClick={() => setChooser(true)}>
              Change decision
            </Button>
          </SpaceBetween>
        )}

        {/* #C6: FYI link to the linked/placeholder SOC 1 + its status. Doesn't
            change this card's Done badge — the "Get SOC 1 for X" action item is
            what stays open until that SOC 1 is uploaded. Gated on coverStatus so a
            DELETED covering report (gone from the reports list) shows no dead link —
            the card has already reopened via its action item. */}
        {coveredBy && coverStatus && (
          <Box>
            <Box variant="awsui-key-label">Subservice SOC 1</Box>
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="inline-link" onClick={() => onOpenReport?.(coveredBy)}>
                {coverName ?? "Open the SOC 1 report"}
              </Button>
              <StatusIndicator type={COVER_STATUS[coverStatus].type}>
                {COVER_STATUS[coverStatus].label}
              </StatusIndicator>
            </SpaceBetween>
            {coverStatus === "awaiting_upload" && (
              <Box variant="small" color="text-body-secondary" padding={{ top: "xxs" }}>
                Requested — this carve-out is done here; the SOC 1 is tracked as its own
                awaiting-upload report. Open it to upload.
              </Box>
            )}
          </Box>
        )}

        {showChooser && (
          <SpaceBetween direction="horizontal" size="xs">
            <Button disabled={busy} onClick={() => setRequesting(true)}>
              Request SOC 1
            </Button>
            <Button disabled={busy} onClick={() => setNaPending(true)}>
              Not applicable → document N/A
            </Button>
            {chooser && (
              <Button variant="inline-link" disabled={busy} onClick={() => setChooser(false)}>
                Cancel
              </Button>
            )}
          </SpaceBetween>
        )}

        {requesting && (
          <Box>
            <Box variant="awsui-key-label">
              Request SOC 1 — open an action item to obtain it, or link an existing report
            </Box>
            <SpaceBetween size="xs">
              <Select
                selectedOption={reportOptions.find((o) => o.value === coverPick) ?? reportOptions[0]!}
                onChange={({ detail }) => setCoverPick(detail.selectedOption.value as string)}
                options={reportOptions}
                disabled={busy}
              />
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="primary"
                  loading={busy}
                  disabled={busy}
                  onClick={() =>
                    void decide(true, coverPick === REQUEST_SENTINEL ? {} : { coveringReportId: coverPick })
                  }
                >
                  Confirm
                </Button>
                <Button onClick={() => setRequesting(false)} disabled={busy}>
                  Cancel
                </Button>
              </SpaceBetween>
            </SpaceBetween>
          </Box>
        )}

        {naPending && (
          <SpaceBetween size="xs">
            <Alert type="info" header="Document the N/A rationale">
              Add a comment below explaining why this subservice organization is not applicable to your scope
              — it's recorded as N/A once you submit.
            </Alert>
            {/* #119: an obvious escape back to the decision buttons (Request SOC 1 /
                document N/A) — the old inline-link buried in the alert text read as
                trapped once the comment composer opened. */}
            <Button variant="link" disabled={busy} onClick={() => setNaPending(false)}>
              ← Back to the decision
            </Button>
          </SpaceBetween>
        )}
        <Box>
          <Box variant="awsui-key-label">Management response</Box>
          <CommentThread
            kind="subservice"
            targetId={co.key}
            onChange={onUpdated}
            onPosted={naPending ? () => void decide(false) : undefined}
            autoOpen={naPending}
            helpText={
              naPending
                ? "Document why this subservice organization is not applicable to your scope, then submit."
                : undefined
            }
          />
        </Box>
      </SpaceBetween>
    </ExpandableSection>
  );
}
